import { useStudioStore } from '@/store/studio-store'
import { checkStudioGenerationReadiness } from '@/features/generation-readiness/studio-readiness-check'
import type { ReadinessReport } from '@/features/generation-readiness/check-readiness'
import {
  appendProjectChatThreadTurn,
  beginProjectChatGeneration,
  projectChatOutcomeIdForSourceMessage,
  readProjectChatThread,
  replaceProjectChatTemplateWithReadiness,
  type ProjectChatGenerationAuthority,
  type ProjectChatThread,
} from '@/features/chat/project-chat-thread'
import { readPersistedApplicationOntologyGenerationSelection } from '@/features/application-ontology/application-ontology-generation'

/**
 * Task 5.5 (ontology-ux-contract cycle) — the chat presentation layer for
 * generation readiness. Spec §3: "생성 시작 시 채팅이 이 화면(패널)을 먼저
 * 제시한다."
 *
 * Every in-chat generation entry (composer app-creation branch, suggestion
 * chips, template-proposal decline, failed-generation retry) calls THIS
 * instead of `startAppGenerationFromPrompt` directly. It:
 *
 *   1. runs the ONE shared production readiness judgment
 *      (`checkStudioGenerationReadiness` — the same factory the WebMCP
 *      registration and the Task 4 choke point consume),
 *   2. appends a `generation-readiness` chat message so `<ReadinessPanel>`
 *      renders IN the conversation, and
 *   3. does NOT start the candidate lifecycle. Ever. Even when the report is
 *      clean — presenting on clean is the point (no auto-skip; auto mode is
 *      explicitly future scope).
 *
 * The candidate lifecycle starts only when the user clicks the panel's
 * generate button, which invokes the `proceed` continuation registered here
 * — the CALL SITE's own existing start block, verbatim (its abort
 * controller, its Stop-button state, its failure surfacing). That is how
 * "기존 생성 경로가 그대로 호출된다" holds without duplicating any start
 * logic into the panel: the panel resumes the caller, it does not re-derive
 * the call.
 *
 * The Task 4 choke point inside `startAppGenerationFromPrompt` stays intact
 * as the second line of defense — programmatic callers (WebMCP tools, the
 * wizard, cogo-xyz, the dev pack-release trigger, the library assistant)
 * bypass this presentation layer deliberately and are guarded there.
 */

/**
 * Proceed continuations, keyed by the appended chat message id. A module
 * Map rather than durable message state because a continuation is a live
 * closure (abort controllers, component feedback callbacks) — not
 * serializable message data. A restored durable readiness turn therefore
 * uses the renderer's authority-preserving fallback instead of pretending
 * this closure survived reload or an HMR boundary swap.
 */
const proceedContinuations = new Map<string, (confirmedPrompt: string) => void>()

export interface PresentGenerationReadinessInput {
  /** The user's generation intent, verbatim. */
  prompt: string
  /**
   * The call site's EXISTING start block, deferred behind the user's
   * explicit generate click. Runs at most once (taken from the registry,
   * not copied).
   *
   * Takes the CONFIRMED prompt — `input.prompt` plus the panel's own
   * readiness-decision block, built by `GenerationReadinessChatCard` at
   * click time (see `buildConfirmedGenerationPrompt`) — rather than
   * closing over `input.prompt` directly. Before this parameter existed,
   * every call site's `proceed` closure captured its prompt BEFORE
   * presentation ever ran, so the confirm click could resume the closure
   * but had no way to influence what it sent: confirming was structurally
   * a no-op on the outgoing prompt (Studio slice-1 prediction P5, design
   * doc `docs/superpowers/specs/2026-08-13-studio-vertical-slice-verification-design.md`).
   * Call sites must pass the received argument to `startAppGenerationFromPrompt`,
   * not their own closed-over prompt, or this fix is cosmetic.
   */
  proceed: (confirmedPrompt: string) => void
  /** Durable recovery for a captured user turn if its scope changes while awaiting readiness. */
  interruptedOutcome?: {
    sourceMessageId: string
    text: string
  }
  /** Replace an existing durable template proposal instead of appending beside it. */
  replaceDurableTurnId?: string
}

export interface PresentedGenerationReadiness {
  messageId: string
  report: ReadinessReport
}

/**
 * Marker this module's own decision block starts with. The E2E mock decompose
 * planner (`vite-plugins/e2e-mock-decompose-planner.ts`, `DECISION_BLOCK`)
 * strips a fixed, enumerated set of these markers before deriving a plan from
 * the prompt — same convention the S1-S4/S7 intake cards already use
 * (`formatFieldVisibilityDecision` etc.). Adding a new decision-block writer
 * means adding its marker there too.
 */
export const GENERATION_READINESS_DECISION_MARKER = '[generation-readiness-confirmed]'

/**
 * Serializes what the readiness panel actually showed the user at the moment
 * they clicked generate — which items were unfilled/advisory and accepted
 * as-is — into the same "document + decision block" shape the S1-S4/S7 intake
 * cards already use. This is what makes confirming a REAL act instead of a
 * relabeled resend: the confirmed prompt states what was reviewed, the
 * unconfirmed one does not.
 */
export function formatReadinessConfirmationDecision(report: ReadinessReport): string {
  const unfilled = report.items.filter((item) => item.kind === 'unfilled')
  const advisory = report.items.filter((item) => item.kind === 'advisory')
  const lines = [
    GENERATION_READINESS_DECISION_MARKER,
    `checkedAt: ${report.checkedAt}`,
    `reviewedItemCount: ${report.items.length}`,
    unfilled.length > 0
      ? `unfilledAcceptedAsIs: ${unfilled.map((item) => item.title).join('; ')}`
      : null,
    advisory.length > 0
      ? `advisoryAcknowledged: ${advisory.map((item) => item.title).join('; ')}`
      : null,
  ].filter((line): line is string => line !== null)
  return lines.join('\n')
}

/** `input.prompt` plus this presentation's own confirmation decision block. */
export function buildConfirmedGenerationPrompt(
  prompt: string,
  report: ReadinessReport,
): string {
  return `${prompt}\n\n${formatReadinessConfirmationDecision(report)}`
}

/**
 * Never rejects: `checkStudioGenerationReadiness` folds every internal
 * failure into a `status:'error'` report, so the panel ALWAYS appears —
 * a broken check presents as the honest error state with retry, never as
 * silence and never as "ready to generate" (fail-closed presentation).
 */
export async function presentGenerationReadinessInChat(
  input: PresentGenerationReadinessInput,
): Promise<PresentedGenerationReadiness | null> {
  const store = useStudioStore.getState()
  const userId = store.session?.user?.id ?? null
  const projectId = store.currentProjectId ?? null
  // Same resolution as the Task 4 choke point: generation may CREATE the
  // project, so "no project open yet" is the normal case, not an error.
  const report = await checkStudioGenerationReadiness(
    store.currentProjectId ?? 'unsaved',
  )
  const current = useStudioStore.getState()
  if (
    (current.session?.user?.id ?? null) !== userId ||
    (current.currentProjectId ?? null) !== projectId
  ) {
    if (userId && projectId && input.interruptedOutcome) {
      appendProjectChatThreadTurn(
        { userId, projectId },
        {
          kind: 'outcome',
          id: projectChatOutcomeIdForSourceMessage(
            input.interruptedOutcome.sourceMessageId,
          ),
          timestamp: Date.now(),
          route: 'generation',
          status: 'failed',
          text: input.interruptedOutcome.text,
          retry: {
            retryable: true,
            route: 'generation',
            intent: input.prompt,
          },
        },
      )
    }
    return null
  }
  const messageId = `generation-readiness-${crypto.randomUUID()}`
  const timestamp = Date.now()
  const selection = readPersistedApplicationOntologyGenerationSelection(
    current.projectSettings,
  )
  const generationAuthority: ProjectChatGenerationAuthority =
    selection.kind === 'selected'
      ? { kind: 'application-ontology', bootstrap: selection.bootstrap }
      : selection.kind === 'unbound'
        ? { kind: 'unbound' }
        : { kind: 'unavailable' }
  const readinessTurn = {
    kind: 'generation-readiness' as const,
    id: messageId,
    timestamp,
    prompt: input.prompt,
    report: { ...report, designSystem: null },
    generationAuthority,
  }
  if (userId && projectId) {
    const scope = { userId, projectId }
    if (input.replaceDurableTurnId) {
      replaceProjectChatTemplateWithReadiness(
        scope,
        input.replaceDurableTurnId,
        readinessTurn,
      )
    } else {
      appendProjectChatThreadTurn(scope, readinessTurn)
    }
  }
  proceedContinuations.set(messageId, input.proceed)
  // The draft prompt reaches the store the moment the request is presented
  // for review — BEFORE any confirm click. `GenerationReadinessChatCard`
  // overwrites this with the confirmed (enriched) prompt at click time, so
  // a caller can compare the two and observe whether confirming changed
  // anything.
  useStudioStore.getState().setLastSubmittedPrompt(input.prompt)
  useStudioStore.getState().appendChatMessage(readinessTurn)
  return { messageId, report }
}

/**
 * One-shot durable claim. A successful claim consumes the continuation so a
 * second resolution attempt cannot start another lifecycle. A persisted
 * ownership conflict returns the owning outcome, while a read/write failure
 * keeps the continuation registered for the same visible action to retry.
 * Restored cards have no live continuation and use the card's fallback path.
 */
export type GenerationReadinessProceedClaim =
  | {
      kind: 'claimed'
      proceed: (confirmedPrompt: string) => void
    }
  | { kind: 'restored' }
  | { kind: 'owned'; thread: ProjectChatThread }
  | { kind: 'persistence-failure' }

export function takeGenerationReadinessProceed(
  messageId: string,
  pendingText: string,
): GenerationReadinessProceedClaim {
  const proceed = proceedContinuations.get(messageId) ?? null
  if (!proceed) return { kind: 'restored' }
  const studio = useStudioStore.getState()
  const userId = studio.session?.user?.id
  const projectId = studio.currentProjectId
  if (userId && projectId) {
    const scope = { userId, projectId }
    const claimed = beginProjectChatGeneration(
      scope,
      messageId,
      pendingText,
      Date.now(),
    )
    if (!claimed) {
      const existing = readProjectChatThread(scope)
      const outcomeId = projectChatOutcomeIdForSourceMessage(messageId)
      if (
        existing?.turns.some(
          (turn) => turn.kind === 'outcome' && turn.id === outcomeId,
        )
      ) {
        proceedContinuations.delete(messageId)
        return { kind: 'owned', thread: existing }
      }
      // A transient read/write failure must leave the live continuation
      // registered so the same visible action can claim and run on retry.
      return { kind: 'persistence-failure' }
    }
  }
  proceedContinuations.delete(messageId)
  return { kind: 'claimed', proceed }
}
